import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './Dashboard.css';
import Help from './Help';

// Import feedback list component
interface FeedbackItem {
  id: string;
  eventId: string;
  userId: string;
  eventName: string;
  eventDate: string;
  overallRating: number;
  platformRating: number;
  organizerRating: number;
  testimonial?: {
    text: string;
    allowPublicUse: boolean;
    participantName: string;
  };
  publicText?: string;
  approvedForPublic?: boolean;
  createdAt: string;
}

interface Event {
  id: string;
  name: string;
  scheduledDate: string;
  city: string;
  status: 'draft' | 'registration' | 'published' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  participantCount: number;
  maxParticipants: number;
  minPairs?: number;
  courses?: {
    appetizer: boolean;
    mainCourse: boolean;
    dessert: boolean;
  };
}

interface OrganizerSettings {
  organizerName: string;
  email: string;
  phone: string;
  pricePerEvent?: number;
  pricePerEventSavedAt?: string;
  netDays?: number;
  applicantType: 'private' | 'business';
  invoiceAddress: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
  orgNumber?: string;
  invoiceReference?: string;
}

const Dashboard: React.FC = () => {
  const { accessToken } = useAuth();
  const location = useLocation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'events' | 'settings' | 'feedback' | 'help'>('events');
  const [settings, setSettings] = useState<OrganizerSettings | null>(null);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalParticipants: 0,
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (accessToken) {
      fetchEvents();
      fetchSettings();
    }
  }, [location, accessToken]);

  const fetchEvents = async () => {
    if (!accessToken) {
      console.error('No access token available');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching events with token:', accessToken?.substring(0, 20) + '...');
      const response = await axios.get<{ events: Event[] }>(`${API_URL}/organizers/events`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      const eventsData: Event[] = response.data.events || [];
      
      // Sort by scheduled date (closest first)
      const sortedEvents = eventsData.sort((a: Event, b: Event) => {
        return new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();
      });
      
      setEvents(sortedEvents);
      
      // Calculate stats
      const totalParticipants = sortedEvents.reduce(
        (sum: number, event: Event) => sum + event.participantCount || 0,
        0
      );
      const activeEvents = sortedEvents.filter(
        (event: Event) => event.status === 'published' || event.status === 'registration' || event.status === 'confirmed'
      ).length;
      
      setStats({
        totalEvents: sortedEvents.length,
        activeEvents,
        totalParticipants,
      });
    } catch (error: any) {
      console.error('Failed to fetch events:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    if (!accessToken) return;

    try {
      const response = await axios.get(`${API_URL}/organizers/settings`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSettings(response.data.settings);
    } catch (error: any) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchFeedback = async () => {
    if (!accessToken) return;

    try {
      setFeedbackLoading(true);
      const response = await axios.get(`${API_URL}/feedback/organizer/events`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setFeedback(response.data.feedback || []);
    } catch (error: any) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="stars-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'star filled' : 'star'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return '#6b7280';
      case 'registration':
        return '#10b981'; // Green for open registration
      case 'confirmed':
        return '#3b82f6'; // Blue for confirmed
      case 'published':
        return 'var(--primary-color)';
      case 'active':
        return '#10b981'; // Green for active
      case 'completed':
        return 'var(--secondary-color)';
      case 'cancelled':
        return '#ef4444'; // Red for cancelled
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Utkast';
      case 'registration':
        return 'Påmelding åpen';
      case 'confirmed':
        return 'Bekreftet';
      case 'published':
        return 'Publisert';
      case 'active':
        return 'Pågår';
      case 'completed':
        return 'Fullført';
      case 'cancelled':
        return 'Avlyst';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <p>Laster inn...</p>
      </div>
    );
  }

  return (
    <div className="organizer-dashboard">
      <div className="dashboard-header">
        <h1>Arrangør</h1>
        <Link to="/arrangor/opprett" className="btn btn-primary">
          + Opprett nytt arrangement
        </Link>
      </div>

      <div className="tabbed-section">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            📅 Mine Arrangementer
          </button>
          <button 
            className={`tab ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('feedback');
              if (feedback.length === 0) fetchFeedback();
            }}
          >
            💬 Tilbakemeldinger
          </button>
          <button 
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Innstillinger
          </button>
          <button 
            className={`tab ${activeTab === 'help' ? 'active' : ''}`}
            onClick={() => setActiveTab('help')}
          >
            📚 Hjelp
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'events' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-content">
                    <h3>{stats.totalEvents}</h3>
                    <p>Totalt arrangementer</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✓</div>
                  <div className="stat-content">
                    <h3>{stats.activeEvents}</h3>
                    <p>Aktive arrangementer</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-content">
                    <h3>{stats.totalParticipants}</h3>
                    <p>Totalt deltakere</p>
                  </div>
                </div>
              </div>

            <div className="events-section">
              {events.length === 0 ? (
                <div className="no-events">
                  <p>Du har ingen arrangementer ennå.</p>
                  <Link to="/arrangor/opprett" className="btn btn-primary">
                    Opprett ditt første arrangement
                  </Link>
                </div>
              ) : (
                <div className="events-grid">
                  {events.map((event) => (
                    <div key={event.id} className="event-card">
                      <div className="event-header">
                        <h3>{event.name}</h3>
                        <span
                          className="event-status"
                          style={{ backgroundColor: getStatusColor(event.status) }}
                        >
                          {getStatusText(event.status)}
                        </span>
                      </div>
                      <div className="event-details">
                        <p>
                          <strong>📅 Dato:</strong>{' '}
                          {new Date(event.scheduledDate).toLocaleDateString('nb-NO', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                        <p>
                          <strong>📍 Sted:</strong> {event.city}
                        </p>
                        <p>
                          <strong>👥 Deltakere:</strong> {event.participantCount} /{' '}
                          {event.maxParticipants}
                        </p>
                        {event.minPairs && (
                          <p style={{ 
                            marginTop: '8px',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            backgroundColor: (event.participantCount / 2) >= event.minPairs ? '#D1FAE5' : '#FEF3C7',
                            color: (event.participantCount / 2) >= event.minPairs ? '#065F46' : '#92400E'
                          }}>
                            {(event.participantCount / 2) >= event.minPairs ? '✅' : '⚠️'} Minimum: {event.minPairs} par
                            {(event.participantCount / 2) < event.minPairs && ` (trenger ${event.minPairs - (event.participantCount / 2)} til)`}
                          </p>
                        )}
                      </div>
                      <div className="event-actions">
                        <Link
                          to={`/arrangor/arrangement/${event.id}`}
                          className="btn btn-secondary"
                        >
                          Administrer
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
          )}

          {activeTab === 'settings' && (
            <div className="settings-section">
              <h2>Innstillinger</h2>
              {settings ? (
                <div className="settings-content">
                  {/* Price Highlight Section */}
                  <div className="price-highlight-section">
                    <div className="price-info">
                      <span className="price-label">Pris per Sykkelfest</span>
                      <span className="price-value">
                        {settings.pricePerEvent 
                          ? `${settings.pricePerEvent.toLocaleString('nb-NO')} kr` 
                          : 'Ikke avtalt ennå'}
                      </span>
                      {settings.pricePerEvent && (
                        <span className="payment-terms">
                          Betalingsbetingelser: netto {settings.netDays || 14} dager
                        </span>
                      )}
                      {settings.pricePerEventSavedAt && (
                        <span className="price-timestamp">
                          Sist oppdatert: {new Date(settings.pricePerEventSavedAt).toLocaleDateString('nb-NO', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Invoice Information */}
                  <div className="settings-group">
                    <h3>Fakturainformasjon</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Arrangørnavn:</span>
                        <span className="info-value">{settings.organizerName}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Type:</span>
                        <span className="info-value">
                          {settings.applicantType === 'private' ? 'Privat' : 'Virksomhet'}
                        </span>
                      </div>
                      {settings.orgNumber && (
                        <div className="info-item">
                          <span className="info-label">Org.nr:</span>
                          <span className="info-value">{settings.orgNumber}</span>
                        </div>
                      )}
                      <div className="info-item full-width">
                        <span className="info-label">Fakturaadresse:</span>
                        <span className="info-value">
                          {settings.invoiceAddress.street}<br/>
                          {settings.invoiceAddress.postalCode} {settings.invoiceAddress.city}<br/>
                          {settings.invoiceAddress.country}
                        </span>
                      </div>
                      {settings.invoiceReference && (
                        <div className="info-item">
                          <span className="info-label">Fakturareferanse:</span>
                          <span className="info-value">{settings.invoiceReference}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="settings-group">
                    <h3>Kontaktinformasjon</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">E-post:</span>
                        <span className="info-value">{settings.email}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Telefon:</span>
                        <span className="info-value">{settings.phone}</span>
                      </div>
                    </div>
                  </div>

                  <p className="settings-note">
                    💡 Kontakt administrator hvis noe av informasjonen må endres.
                  </p>
                </div>
              ) : (
                <p>Laster innstillinger...</p>
              )}
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="feedback-tab-content">
              {feedbackLoading ? (
                <div className="loading-container">Laster tilbakemeldinger...</div>
              ) : feedback.length === 0 ? (
                <div className="no-feedback">
                  <p>Ingen tilbakemeldinger ennå.</p>
                  <p>Tilbakemeldinger vises her etter at deltakere har sendt inn sine vurderinger.</p>
                </div>
              ) : (
                <>
                  {/* Summary Section */}
                  <div className="feedback-summary">
                    <h3>Gjennomsnittsvurderinger</h3>
                    <div className="summary-grid">
                      <div className="summary-item">
                        <label>Opplevelse generelt</label>
                        {renderStars(Math.round(feedback.reduce((sum, f) => sum + f.overallRating, 0) / feedback.length))}
                        <span className="rating-value">
                          {(feedback.reduce((sum, f) => sum + f.overallRating, 0) / feedback.length).toFixed(1)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <label>Plattform</label>
                        {renderStars(Math.round(feedback.reduce((sum, f) => sum + f.platformRating, 0) / feedback.length))}
                        <span className="rating-value">
                          {(feedback.reduce((sum, f) => sum + f.platformRating, 0) / feedback.length).toFixed(1)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <label>Arrangør</label>
                        {renderStars(Math.round(feedback.reduce((sum, f) => sum + f.organizerRating, 0) / feedback.length))}
                        <span className="rating-value">
                          {(feedback.reduce((sum, f) => sum + f.organizerRating, 0) / feedback.length).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Feedback List */}
                  <div className="feedback-list">
                    <h3>Alle tilbakemeldinger ({feedback.length})</h3>
                    {feedback.map((item) => (
                      <div key={item.id} className="feedback-item">
                        <div className="feedback-header-info">
                          <h4>{item.eventName}</h4>
                          <p className="feedback-date">
                            {new Date(item.createdAt).toLocaleDateString('nb-NO', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>

                        <div className="ratings-grid">
                          <div className="rating-item">
                            <label>Opplevelse:</label>
                            {renderStars(item.overallRating)}
                          </div>
                          <div className="rating-item">
                            <label>Plattform:</label>
                            {renderStars(item.platformRating)}
                          </div>
                          <div className="rating-item">
                            <label>Arrangør:</label>
                            {renderStars(item.organizerRating)}
                          </div>
                        </div>

                        {item.testimonial && (
                          <div className="testimonial-section">
                            <h5>Tilbakemelding:</h5>
                            <p className="testimonial-text">{item.testimonial.text}</p>
                            {item.testimonial.allowPublicUse && (
                              <p className="testimonial-info">
                                <em>
                                  Deltaker har godkjent offentlig publisering
                                  {item.testimonial.participantName && (
                                    <> som: {item.testimonial.participantName}</>
                                  )}
                                </em>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'help' && <Help />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
